import React, { useState, useCallback } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import type { DynamicWidgetConfig, QueryResult } from '@/types/dashboard';
import type { VisualizationClickEvent } from './DynamicVisualization';

interface TableVisualizationProps {
  data: QueryResult;
  config: DynamicWidgetConfig;
  /** Callback when a row is clicked (DASH-24.2) */
  onSegmentClick?: (event: VisualizationClickEvent) => void;
}

const ROWS_PER_PAGE = 10;

/**
 * Table Visualization (DASH-8.2, DASH-24.2)
 * Displays detailed records with columns, sort, and pagination
 * DASH-24.2: Added row click handler for drill-down
 */
export function TableVisualization({ data, config, onSegmentClick }: TableVisualizationProps) {
  const [currentPage, setCurrentPage] = useState(0);

  // Expect array of records
  const records = Array.isArray(data.data) ? data.data : [];

  // Handle row click (DASH-24.2)
  const handleRowClick = useCallback(
    (record: any, index: number) => {
      if (onSegmentClick) {
        onSegmentClick({
          value: record.id || index,
          index,
          data: record,
        });
      }
    },
    [onSegmentClick]
  );

  // Pagination
  const totalPages = Math.ceil(records.length / ROWS_PER_PAGE);
  const startIndex = currentPage * ROWS_PER_PAGE;
  const endIndex = startIndex + ROWS_PER_PAGE;
  const paginatedRecords = records.slice(startIndex, endIndex);

  // Determine columns based on data source
  const columns = getColumnsForDataSource(config.dataSource);

  return (
    <div className="h-full flex flex-col">
      <div className="flex-1 overflow-auto">
        <Table>
          <TableHeader>
            <TableRow>
              {columns.map((col) => (
                <TableHead key={col.key}>{col.label}</TableHead>
              ))}
            </TableRow>
          </TableHeader>
          <TableBody>
            {paginatedRecords.length === 0 ? (
              <TableRow>
                <TableCell colSpan={columns.length} className="text-center text-muted-foreground">
                  No records found
                </TableCell>
              </TableRow>
            ) : (
              paginatedRecords.map((record, index) => (
                <TableRow
                  key={index}
                  className="hover:bg-muted/50 transition-colors cursor-pointer"
                  onClick={() => handleRowClick(record, startIndex + index)}
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      handleRowClick(record, startIndex + index);
                    }
                  }}
                >
                  {columns.map((col) => (
                    <TableCell key={col.key}>
                      {formatCellValue(record[col.key], col.type)}
                    </TableCell>
                  ))}
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between pt-4 border-t">
          <div className="text-sm text-muted-foreground">
            Showing {startIndex + 1}-{Math.min(endIndex, records.length)} of {records.length}
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setCurrentPage(p => Math.max(0, p - 1))}
              disabled={currentPage === 0}
            >
              <ChevronLeft className="w-4 h-4" />
            </Button>
            <div className="text-sm text-muted-foreground">
              Page {currentPage + 1} of {totalPages}
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setCurrentPage(p => Math.min(totalPages - 1, p + 1))}
              disabled={currentPage === totalPages - 1}
            >
              <ChevronRight className="w-4 h-4" />
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * Get column definitions based on data source
 */
function getColumnsForDataSource(dataSource: string) {
  switch (dataSource) {
    case 'projects':
      return [
        { key: 'project_name', label: 'Project', type: 'text' },
        { key: 'status', label: 'Status', type: 'text' },
        { key: 'assigned_to', label: 'Assigned To', type: 'text' },
        { key: 'created_at', label: 'Created', type: 'date' },
      ];

    case 'appointments':
      return [
        { key: 'lead_name', label: 'Lead', type: 'text' },
        { key: 'disposition', label: 'Disposition', type: 'text' },
        { key: 'date_time', label: 'Date/Time', type: 'datetime' },
      ];

    case 'tasks':
      return [
        { key: 'title', label: 'Task', type: 'text' },
        { key: 'status', label: 'Status', type: 'text' },
        { key: 'due_date', label: 'Due Date', type: 'date' },
        { key: 'assignee_id', label: 'Assigned To', type: 'text' },
      ];

    case 'notifications':
      return [
        { key: 'title', label: 'Title', type: 'text' },
        { key: 'type', label: 'Type', type: 'text' },
        { key: 'created_at', label: 'Time', type: 'date' },
      ];

    default:
      return [
        { key: 'id', label: 'ID', type: 'text' },
        { key: 'created_at', label: 'Created', type: 'date' },
      ];
  }
}

/**
 * Format cell value based on type
 */
function formatCellValue(value: any, type: string): string {
  if (value === null || value === undefined) {
    return '-';
  }

  switch (type) {
    case 'date':
      try {
        return formatDistanceToNow(new Date(value), { addSuffix: true });
      } catch {
        return String(value);
      }

    case 'datetime':
      return new Date(value).toLocaleString('en-US', {
        month: 'short',
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
      });

    case 'text':
    default:
      return String(value);
  }
}
